import { useEffect, useMemo } from 'react'
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Logo } from 'components/common'
import { usePageTracking } from 'hooks/use-page-tracking'
import { useStore } from 'hooks/useStore'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	clearPendingInvite,
	clearPendingPartnerConsent,
	getPendingInviteId,
	getPendingPartnerConsentPath,
	setPendingInvite,
	setPendingPartnerConsent,
} from 'services/auth'
import { inviteIdFromRedirectPath } from 'utils/invite-accept'
import { partnerConsentRequestIn } from 'utils/partner-consent-request'
import { RECEIVED_TICKET_PARAM } from 'pages/Events/RSVP/received-ticket'
import { PartnerSignInFrame } from 'containers/partner-consent'

/** Completes the partner banner's "… to connect [partner] to your Loyalink account." for each auth page. */
const partnerBannerAction = (pathname: string) => {
	if (pathname.startsWith('/auth/sign-up')) return 'Sign up'
	if (pathname.startsWith('/auth/confirm-email')) return 'Confirm your email'
	return 'Sign in'
}

export function AuthLayout() {
	usePageTracking()
	const { loadingLogout } = useStore()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('to')
	const location = useLocation()
	const navigate = useNavigate()
	// "Forgot your password?" and the reset email lose ?to=, so a saved partner request stands in for it.
	const returnTo = redirectTo || getPendingPartnerConsentPath()
	const partnerRequest = useMemo(() => partnerConsentRequestIn(returnTo), [returnTo])

	useEffect(() => {
		if (!redirectTo) return
		if (partnerConsentRequestIn(redirectTo)) setPendingPartnerConsent(redirectTo)
		else clearPendingPartnerConsent()
	}, [redirectTo])

	// Router state only survives in-app navigation within one tab. Re-seed a pending invite (new tab from a
	// reset/confirm email, forgot-password, social login, reload) so the guards can resume it after auth.
	// An explicit ?to= / ?a= destination wins, and supersedes any stored invite: auth links such as forgot-password
	// drop that query, so the invite must not be re-seeded on a later page.
	useEffect(() => {
		// The global 401 handler (graphql/apollo.ts) clears storage and sends `?to=/accept-invite/<id>`. `to` still
		// drives the post-auth redirect; carry the id in state so the invite-only options (phone sign-in) stay visible.
		const redirectInviteId = inviteIdFromRedirectPath(redirectTo)
		if (redirectInviteId) {
			// PublicGuard resumes a bare /accept-invite/<id> once inviteId is set, and the 401 handler has cleared
			// storage, so save the full path: the invite page restores src, d and backTo from it.
			setPendingInvite(redirectTo as string)
			if (location.state?.inviteId !== redirectInviteId) {
				navigate(location, { replace: true, state: { ...location.state, inviteId: redirectInviteId } })
			}
			return
		}
		if (redirectTo || searchParams.get('a')) {
			clearPendingInvite()
			return
		}
		if (location.state?.inviteId) return
		// Keep email confirmation active until verification finishes: PublicGuard would otherwise redirect to the
		// invite while the token is still being verified. That page resumes the invite via its Continue button.
		if (location.pathname === '/auth/confirm-email') return
		const inviteId = getPendingInviteId()
		if (inviteId) navigate(location, { replace: true, state: { ...location.state, inviteId } })
	}, [location.pathname])

	// The sign-up forms pair gender and date of birth side by side, which clips the date field at max-w-md.
	const isSignUp = location.pathname.startsWith('/auth/sign-up')
	const card = (
		<div className={`w-full ${isSignUp ? 'max-w-lg' : 'max-w-md'} rounded-2xl border border-translucent-border bg-secondary-surface/[0.3] backdrop-blur-lg p-6 xs:p-8`}>
			<Outlet />
		</div>
	)

	// The padding makes a 44px tap target; the negative margin keeps the line where it was.
	// A received-ticket URL sends a signed-out visitor straight back to login, so it is no way "back".
	const backLink = returnTo && !returnTo.includes(`${RECEIVED_TICKET_PARAM}=`) && (
		<Link
			to={returnTo}
			className="-my-3 inline-block py-3 text-sm text-secondary-text hover:text-primary-text transition-colors"
		>
			← Go back to where you were
		</Link>
	)

	return (
		<div className="h-screen">
			<div className="min-h-full flex flex-col items-center justify-center px-4 xs:px-8 py-8">
				<div className="mb-8 -ml-7">
					<a href={import.meta.env.VITE_LANDING_URL}>
						<Logo height={48} variant="full" />
					</a>
				</div>
				{partnerRequest ? (
					<PartnerSignInFrame
						request={partnerRequest}
						action={partnerBannerAction(location.pathname)}
						fallback={backLink}
					>
						{card}
					</PartnerSignInFrame>
				) : (
					<>
						{card}
						{backLink && <div className="mt-4 text-center">{backLink}</div>}
					</>
				)}
			</div>
			{loadingLogout && (
				<div className="flex justify-center items-center py-4 size-full fixed top-0 left-0 bg-primary-surface/[0.4] backdrop-blur-lg bg-opacity-90 z-[9999]">
					<FontAwesomeIcon icon={['fas', 'spinner']} size="2x" spin className="text-white" />
				</div>
			)}
		</div>
	)
}
